"use client";

import { useEffect } from "react";
import type { ActionReveal } from "@/lib/api";
import { useChatRevealStore } from "@/lib/stores/chatReveal";

function escapeRevealId(id: string): string {
  if (typeof window !== "undefined" && window.CSS?.escape) {
    return window.CSS.escape(id);
  }
  return id.replace(/["\\]/g, "\\$&");
}

export function useChatRevealHighlight(
  surface: ActionReveal["surface"] | (string & {}),
  depsKey = "",
) {
  const active = useChatRevealStore((state) => state.active);
  const clearActive = useChatRevealStore((state) => state.clearActive);

  useEffect(() => {
    if (!active || active.surface !== surface) return;

    let timerId = 0;
    const run = () => {
      const escapedId = escapeRevealId(active.id);
      const selector = `[data-reveal-id="${escapedId}"], [data-reveal-ids~="${escapedId}"]`;
      const element = document.querySelector<HTMLElement>(selector);
      if (!element) return;

      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.classList.remove("animate-land-pulse");
      void element.offsetWidth;
      element.classList.add("animate-land-pulse");

      timerId = window.setTimeout(() => {
        element.classList.remove("animate-land-pulse");
        clearActive(active.id);
      }, 1500);
    };

    const frameId = window.requestAnimationFrame(run);
    return () => {
      window.cancelAnimationFrame(frameId);
      if (timerId) window.clearTimeout(timerId);
    };
  }, [active, clearActive, depsKey, surface]);
}
